// Firestore Service - Database operations for sharing and metadata
import { db } from '../config/firebase.js';
import {
  doc,
  setDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  deleteDoc,
  deleteField,
  serverTimestamp,
  increment,
  arrayUnion,
  arrayRemove,
  addDoc,
  writeBatch,
  orderBy,
  limit,
  collectionGroup,
  runTransaction
} from 'firebase/firestore';
import { cacheService } from './cache.service';

class FirestoreService {
  // ============ USER MANAGEMENT ============

  // Create or update user document
  async createOrUpdateUser(user, googleCalendarId = null) {
    const userRef = doc(db, 'users', user.uid);
    const existingUser = await getDoc(userRef);

    if (!existingUser.exists()) {
      // New user - create with initial stats
      await setDoc(userRef, {
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        googleCalendarId,
        experiencesCount: 0,
        shareCount: 0,
        userPersonalStickers: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } else {
      // Existing user - update without resetting stats
      await updateDoc(userRef, {
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        ...(googleCalendarId && { googleCalendarId }),
        updatedAt: serverTimestamp()
      });
    }

    return userRef.id;
  }

  // Get user document
  async getUser(userId) {
    const userRef = doc(db, 'users', userId);
    const snapshot = await getDoc(userRef);
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
  }

  // Batch lookup users by email addresses (for attendee sync)
  // Returns a map of { email → { userId, displayName, photoURL } }
  // Firestore 'in' limit is 30, so queries are chunked automatically.
  async getUsersByEmails(emails) {
    if (!emails || emails.length === 0) return {};

    const emailToUser = {};
    const uniqueEmails = [...new Set(emails)];
    const CHUNK_SIZE = 30;

    for (let i = 0; i < uniqueEmails.length; i += CHUNK_SIZE) {
      const chunk = uniqueEmails.slice(i, i + CHUNK_SIZE);
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', 'in', chunk));
      const snapshot = await getDocs(q);
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        emailToUser[data.email] = {
          userId: docSnap.id,
          displayName: data.displayName || null,
          photoURL: data.photoURL || null,
        };
      });
    }

    return emailToUser;
  }

  // Update user settings
  async updateUserSettings(userId, settings) {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      settings,
      updatedAt: serverTimestamp()
    });
  }

  // ============ GOOGLE TOKEN STORAGE ============

  // Save Google access token for a user account
  async saveGoogleToken(userId, email, accessToken) {
    const tokenRef = doc(db, 'users', userId, 'googleTokens', email);
    await setDoc(tokenRef, {
      accessToken,
      email,
      savedAt: Date.now(),
      expiresAt: Date.now() + (60 * 60 * 1000), // 1 hour from now
      updatedAt: serverTimestamp()
    });
  }

  // Get all Google tokens for a user
  // Returns all tokens regardless of expiry — let the 401 handler manage actual expiry
  async getGoogleTokens(userId) {
    const tokensRef = collection(db, 'users', userId, 'googleTokens');
    const snapshot = await getDocs(tokensRef);
    const tokens = {};
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      tokens[data.email] = {
        accessToken: data.accessToken,
        savedAt: data.savedAt,
        expiresAt: data.expiresAt,
        possiblyExpired: data.expiresAt <= Date.now()
      };
    });
    return tokens;
  }

  // Delete a Google token
  async deleteGoogleToken(userId, email) {
    const tokenRef = doc(db, 'users', userId, 'googleTokens', email);
    await deleteDoc(tokenRef);
  }

  // ============ CALENDAR SHARING ============

  // Create a shareable calendar entry
  async createShareableCalendar(userId, googleCalendarId, title) {
    const shareId = this.generateShareId();
    const calendarRef = doc(db, 'calendars', shareId);

    await setDoc(calendarRef, {
      ownerId: userId,
      googleCalendarId,
      title: title || 'Grownups Calendar 2026',
      createdAt: serverTimestamp(),
      lastSyncAt: serverTimestamp(),
      isPublic: false,
      cachedEvents: []
    });

    return shareId;
  }

  // Get calendar by share ID
  async getCalendarByShareId(shareId) {
    const calendarRef = doc(db, 'calendars', shareId);
    const snapshot = await getDoc(calendarRef);
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
  }

  // Update cached events (for anonymous viewing)
  async updateCachedEvents(shareId, events) {
    const calendarRef = doc(db, 'calendars', shareId);
    await updateDoc(calendarRef, {
      cachedEvents: events,
      lastSyncAt: serverTimestamp()
    });
  }

  // Get calendars owned by user
  async getUserCalendars(userId) {
    const calendarsRef = collection(db, 'calendars');
    const q = query(calendarsRef, where('ownerId', '==', userId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  }

  // ============ SHARE LINKS ============

  // Create a share link
  async createShare(calendarId, permission = 'view', sharedWith = 'public') {
    const shareRef = doc(collection(db, 'shares'));
    await setDoc(shareRef, {
      calendarId,
      sharedWith,
      permission,
      createdAt: serverTimestamp()
    });
    return shareRef.id;
  }

  // Get shares for a calendar
  async getSharesByCalendar(calendarId) {
    const sharesRef = collection(db, 'shares');
    const q = query(sharesRef, where('calendarId', '==', calendarId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  }

  // Delete a share
  async deleteShare(shareId) {
    const shareRef = doc(db, 'shares', shareId);
    await deleteDoc(shareRef);
  }

  // ============ PERMISSIONS ============

  // Check if user can edit calendar
  async canUserEdit(userId, calendarId) {
    const calendar = await this.getCalendarByShareId(calendarId);

    if (!calendar) return false;

    // Owner can always edit
    if (calendar.ownerId === userId) return true;

    // Check for edit permission in shares
    const shares = await this.getSharesByCalendar(calendarId);
    return shares.some(share =>
      share.permission === 'edit' &&
      (share.sharedWith === 'public' || share.sharedWith === userId)
    );
  }

  // ============ EVENT TRACKING ============

  // Generate event ID from name (slugify)
  slugifyEventName(name) {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-');
  }

  // Track when an event is added to calendar
  async trackEventAdded(eventName, category, userId = null) {
    const eventId = this.slugifyEventName(eventName);
    const eventRef = doc(db, 'events', eventId);
    const existingEvent = await getDoc(eventRef);

    if (!existingEvent.exists()) {
      // Create new event doc
      await setDoc(eventRef, {
        name: eventName,
        category: category,
        addCount: 1,
        activeCount: 1,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } else {
      // Increment counters
      await updateDoc(eventRef, {
        addCount: increment(1),
        activeCount: increment(1),
        updatedAt: serverTimestamp()
      });
    }

    // Update user stats and user_events if logged in
    if (userId) {
      await this.incrementUserAdventure(userId);
      await this.addUserEvent(userId, eventId);
    }

    // Update global stats
    await this.incrementGlobalEventCount();

    return eventId;
  }

  // Track when an event is removed from calendar
  async trackEventRemoved(eventName, userId = null) {
    const eventId = this.slugifyEventName(eventName);
    const eventRef = doc(db, 'events', eventId);
    const existingEvent = await getDoc(eventRef);

    if (existingEvent.exists()) {
      // Decrement activeCount (but not addCount)
      const currentActive = existingEvent.data().activeCount || 0;
      if (currentActive > 0) {
        await updateDoc(eventRef, {
          activeCount: increment(-1),
          updatedAt: serverTimestamp()
        });
      }
    }

    // Update user stats if logged in
    if (userId) {
      await this.decrementUserAdventure(userId);
      await this.removeUserEvent(userId, eventId);
    }
  }

  // Get event stats
  async getEventStats(eventName) {
    const eventId = this.slugifyEventName(eventName);
    const eventRef = doc(db, 'events', eventId);
    const snapshot = await getDoc(eventRef);
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
  }

  // ============ EVENT PARTICIPATION (for Event Detail Modal) ============

  /**
   * Track when a user joins/participates in a themed calendar event
   * @param {string} eventId - Unique event ID (from JSON data)
   * @param {string} calendarId - Calendar ID the event belongs to
   * @param {string} userId - User ID (optional for anonymous tracking)
   * @returns {Promise<number>} - Updated participant count
   */
  async trackEventParticipation(eventId, calendarId, userId = null) {
    const participationRef = doc(db, 'event_participation', eventId);
    const existingDoc = await getDoc(participationRef);

    if (!existingDoc.exists()) {
      // Create new participation record
      await setDoc(participationRef, {
        eventId,
        calendarId,
        participantCount: 1,
        participantIds: userId ? [userId] : [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return 1;
    }

    const data = existingDoc.data();
    const participantIds = data.participantIds || [];

    // Check if user already participated (prevent double-counting)
    if (userId && participantIds.includes(userId)) {
      return data.participantCount || 0;
    }

    // Update participation record
    const updates = {
      participantCount: increment(1),
      updatedAt: serverTimestamp()
    };

    if (userId) {
      updates.participantIds = arrayUnion(userId);
    }

    await updateDoc(participationRef, updates);
    return (data.participantCount || 0) + 1;
  }

  /**
   * Get participant count for an event
   * @param {string} eventId - Unique event ID
   * @returns {Promise<number>} - Participant count
   */
  async getEventParticipantCount(eventId) {
    const participationRef = doc(db, 'event_participation', eventId);
    const snapshot = await getDoc(participationRef);

    if (!snapshot.exists()) {
      return 0;
    }

    return snapshot.data().participantCount || 0;
  }

  /**
   * Get participant counts for multiple events (batch fetch)
   * @param {array} eventIds - Array of event IDs
   * @returns {Promise<object>} - Map of eventId -> participantCount
   */
  async getEventParticipantCounts(eventIds) {
    if (!eventIds || eventIds.length === 0) {
      return {};
    }

    const counts = {};

    // Firestore doesn't support "IN" queries for doc IDs, so fetch individually
    // In production, consider using a Cloud Function for batch reads
    await Promise.all(
      eventIds.map(async (eventId) => {
        counts[eventId] = await this.getEventParticipantCount(eventId);
      })
    );

    return counts;
  }

  /**
   * Check if user has participated in an event
   * @param {string} eventId - Event ID
   * @param {string} userId - User ID
   * @returns {Promise<boolean>} - True if user has participated
   */
  async hasUserParticipated(eventId, userId) {
    if (!userId) return false;

    const participationRef = doc(db, 'event_participation', eventId);
    const snapshot = await getDoc(participationRef);

    if (!snapshot.exists()) {
      return false;
    }

    const participantIds = snapshot.data().participantIds || [];
    return participantIds.includes(userId);
  }

  // ============ LINKED ACCOUNTS ============

  // Add a linked Google account to user's profile
  async addLinkedAccount(userId, accountData) {
    const userRef = doc(db, 'users', userId);
    const linkedAccount = {
      email: accountData.email,
      displayName: accountData.displayName || accountData.name,
      photoURL: accountData.photoURL || accountData.picture,
      uid: accountData.uid,
      provider: 'google',
      linkedAt: new Date().toISOString()
    };

    await updateDoc(userRef, {
      linkedAccounts: arrayUnion(linkedAccount),
      updatedAt: serverTimestamp()
    });

    return linkedAccount;
  }

  // Remove a linked account from user's profile
  async removeLinkedAccount(userId, email) {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const linkedAccounts = userDoc.data().linkedAccounts || [];
      const accountToRemove = linkedAccounts.find(acc => acc.email === email);

      if (accountToRemove) {
        await updateDoc(userRef, {
          linkedAccounts: arrayRemove(accountToRemove),
          updatedAt: serverTimestamp()
        });
      }
    }
  }

  // Get all linked accounts for a user
  async getLinkedAccounts(userId) {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      return userDoc.data().linkedAccounts || [];
    }
    return [];
  }

  // Check if an account is already linked
  async isAccountLinked(userId, email) {
    const linkedAccounts = await this.getLinkedAccounts(userId);
    return linkedAccounts.some(acc => acc.email === email);
  }

  // ============ USER PROFILE (GRO-41) ============

  /**
   * Update user profile (embedded in user doc)
   * @param {string} userId - User ID
   * @param {object} profileData - Profile data (name, location, hobbies, interests, etc.)
   */
  async updateUserProfile(userId, profileData) {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      profile: profileData,
      updatedAt: serverTimestamp()
    }, { merge: true });

    // Update cache
    cacheService.updateUser({ profile: profileData });
  }

  /**
   * Get user profile from user doc
   * @param {string} userId - User ID
   * @returns {object|null} - Profile data
   */
  async getUserProfile(userId) {
    const user = await this.getUser(userId);
    return user?.profile || null;
  }

  // ============ USER PREFERENCES (GRO-41) ============

  /**
   * Update user preferences (embedded in user doc)
   * @param {string} userId - User ID
   * @param {object} preferences - Preferences (theme, colors, fonts, etc.)
   */
  async updateUserPreferences(userId, preferences) {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      preferences: preferences,
      updatedAt: serverTimestamp()
    }, { merge: true });

    // Update cache
    cacheService.updateUser({ preferences });
  }

  /**
   * Update specific preference fields (merge)
   * @param {string} userId - User ID
   * @param {object} updates - Preference fields to update
   */
  async updatePreferenceFields(userId, updates) {
    const userRef = doc(db, 'users', userId);
    const user = await this.getUser(userId);
    const currentPrefs = user?.preferences || {};
    const newPrefs = { ...currentPrefs, ...updates };

    await updateDoc(userRef, {
      preferences: newPrefs,
      updatedAt: serverTimestamp()
    });

    // Update cache
    cacheService.updateUser({ preferences: newPrefs });
  }

  /**
   * Get user preferences
   * @param {string} userId - User ID
   * @returns {object|null} - Preferences data
   */
  async getUserPreferences(userId) {
    const user = await this.getUser(userId);
    return user?.preferences || null;
  }

  // ============ USER DESIGN PREFERENCES (GRO-41) ============

  /**
   * Update design mode preferences
   * @param {string} userId - User ID
   * @param {object} designPrefs - Design preferences (canvas settings, etc.)
   */
  async updateDesignPreferences(userId, designPrefs) {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      designPreferences: designPrefs,
      updatedAt: serverTimestamp()
    });

    // Update cache
    cacheService.updateUser({ designPreferences: designPrefs });
  }

  // ============ CALENDAR SETTINGS (GRO-49) ============

  /**
   * Update calendar settings (selectedCalendars, colors, etc.)
   * @param {string} userId - User ID
   * @param {object} calendarSettings - Calendar settings
   */
  async updateCalendarSettings(userId, calendarSettings) {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      calendarSettings: calendarSettings,
      updatedAt: serverTimestamp()
    }, { merge: true });

    // Update cache
    cacheService.updateUser({ calendarSettings });
  }

  /**
   * Get calendar settings
   * @param {string} userId - User ID
   * @returns {object|null} - Calendar settings
   */
  async getCalendarSettings(userId) {
    const user = await this.getUser(userId);
    return user?.calendarSettings || null;
  }

  // ============ LINKED ACCOUNTS SUBCOLLECTION (GRO-42) ============

  /**
   * Add linked account to subcollection (new structure)
   * @param {string} userId - User ID
   * @param {object} accountData - Account data
   */
  async addLinkedAccountToSubcollection(userId, accountData) {
    const accountRef = doc(db, 'users', userId, 'linkedAccounts', accountData.email);
    const linkedAccount = {
      email: accountData.email,
      displayName: accountData.displayName || accountData.name,
      photoURL: accountData.photoURL || accountData.picture,
      uid: accountData.uid,
      provider: 'google',
      linkedAt: serverTimestamp()
    };

    await setDoc(accountRef, linkedAccount);

    // Update cache
    cacheService.addLinkedAccount(linkedAccount);

    return linkedAccount;
  }

  /**
   * Get linked accounts from subcollection (new structure)
   * @param {string} userId - User ID
   * @returns {array} - Array of linked accounts
   */
  async getLinkedAccountsFromSubcollection(userId) {
    // Check cache first
    const cached = cacheService.getLinkedAccounts();
    if (cached) return cached;

    const accountsRef = collection(db, 'users', userId, 'linkedAccounts');
    const snapshot = await getDocs(accountsRef);
    const accounts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Cache result
    cacheService.setLinkedAccounts(accounts);

    return accounts;
  }

  /**
   * Remove linked account from subcollection (new structure)
   * @param {string} userId - User ID
   * @param {string} email - Email of account to remove
   */
  async removeLinkedAccountFromSubcollection(userId, email) {
    const accountRef = doc(db, 'users', userId, 'linkedAccounts', email);
    await deleteDoc(accountRef);

    // Update cache
    cacheService.removeLinkedAccount(email);
  }

  // ============ ONBOARDING SUBCOLLECTION (GRO-43) ============

  /**
   * Save onboarding choices to subcollection
   * @param {string} userId - User ID
   * @param {object} onboardingData - Onboarding choices
   */
  async saveOnboardingChoices(userId, onboardingData) {
    const onboardingRef = doc(db, 'users', userId, 'onboarding', 'choices');
    await setDoc(onboardingRef, {
      ...onboardingData,
      completedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  }

  /**
   * Get onboarding choices (lazy load - not cached by default)
   * @param {string} userId - User ID
   * @returns {object|null} - Onboarding data
   */
  async getOnboardingChoices(userId) {
    const onboardingRef = doc(db, 'users', userId, 'onboarding', 'choices');
    const snapshot = await getDoc(onboardingRef);
    return snapshot.exists() ? snapshot.data() : null;
  }

  /**
   * Check if onboarding is complete
   * @param {string} userId - User ID
   * @returns {boolean}
   */
  async isOnboardingComplete(userId) {
    const choices = await this.getOnboardingChoices(userId);
    return choices?.isOnboardingComplete === true;
  }

  // ============ TASK LISTS COLLECTION (GRO-45) ============

  /**
   * Create a new task list
   * @param {string} userId - User ID
   * @param {object} taskListData - Task list data
   * @returns {string} - Task list ID
   */
  async createTaskList(userId, taskListData) {
    const taskListRef = doc(collection(db, 'taskLists', userId, 'lists'));
    const taskList = {
      id: taskListRef.id,
      title: taskListData.title || 'New List',
      color: taskListData.color || null,
      textcolor: taskListData.textcolor || null,
      tasks: taskListData.tasks || [],
      scheduledtasks: taskListData.scheduledtasks || [],
      completedtasks: taskListData.completedtasks || [],
      created: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(taskListRef, taskList);

    // Update cache
    cacheService.updateTaskList(taskListRef.id, { ...taskList, id: taskListRef.id });

    return taskListRef.id;
  }

  /**
   * Update a task list
   * @param {string} userId - User ID
   * @param {string} taskListId - Task list ID
   * @param {object} updates - Fields to update
   */
  async updateTaskList(userId, taskListId, updates) {
    const taskListRef = doc(db, 'taskLists', userId, 'lists', taskListId);
    await setDoc(taskListRef, {
      ...updates,
      updatedAt: serverTimestamp()
    }, { merge: true });

    // Update cache
    const current = cacheService.getTaskLists() || [];
    const existing = current.find(t => t.id === taskListId);
    if (existing) {
      cacheService.updateTaskList(taskListId, { ...existing, ...updates });
    }
  }

  /**
   * Delete a task list
   * @param {string} userId - User ID
   * @param {string} taskListId - Task list ID
   */
  async deleteTaskList(userId, taskListId) {
    const taskListRef = doc(db, 'taskLists', userId, 'lists', taskListId);
    await deleteDoc(taskListRef);

    // Update cache
    cacheService.removeTaskList(taskListId);
  }

  /**
   * Get all task lists for a user
   * @param {string} userId - User ID
   * @param {boolean} useCache - Whether to use cache (default: true)
   * @returns {array} - Array of task lists
   */
  async getTaskLists(userId, useCache = true) {
    // Check cache first
    if (useCache) {
      const cached = cacheService.getTaskLists();
      if (cached) return cached;
    }

    const listsRef = collection(db, 'taskLists', userId, 'lists');
    const snapshot = await getDocs(listsRef);
    const lists = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Cache result
    cacheService.setTaskLists(lists);

    return lists;
  }

  /**
   * Get a single task list
   * @param {string} userId - User ID
   * @param {string} taskListId - Task list ID
   * @returns {object|null} - Task list data
   */
  async getTaskList(userId, taskListId) {
    const taskListRef = doc(db, 'taskLists', userId, 'lists', taskListId);
    const snapshot = await getDoc(taskListRef);
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
  }

  // ============ CALENDAR DATA / JOURNAL (GRO-51) ============

  /**
   * Check if journal content is empty (no blocks or only empty blocks)
   * @param {object} journalData - Journal content
   * @returns {boolean} - True if empty
   */
  _isJournalEmpty(journalData) {
    if (!journalData?.content?.blocks) return true;
    if (journalData.content.blocks.length === 0) return true;

    // Check if all blocks are empty paragraphs
    const hasContent = journalData.content.blocks.some(block => {
      if (block.type === 'paragraph') {
        return block.data?.text && block.data.text.trim() !== '';
      }
      // Non-paragraph blocks (headers, lists, etc.) are considered content
      return true;
    });

    return !hasContent;
  }

  /**
   * Save journal entry for a specific date
   * @param {string} userId - User ID
   * @param {string} date - Date key (YYYY-MM-DD)
   * @param {object} journalData - Journal content
   */
  async saveJournalEntry(userId, date, journalData) {
    const dailyRef = doc(db, 'calendarData', userId, 'daily', date);
    const existing = await getDoc(dailyRef);
    const isEmpty = this._isJournalEmpty(journalData);

    if (existing.exists()) {
      if (isEmpty) {
        // User cleared the journal - delete the journal field to save space
        await updateDoc(dailyRef, {
          journal: deleteField(),
          updatedAt: serverTimestamp()
        });
        // Clear from cache
        cacheService.removeJournalEntry(date);
      } else {
        // Update existing document with content
        await updateDoc(dailyRef, {
          journal: {
            ...journalData,
            updatedAt: new Date().toISOString()
          },
          updatedAt: serverTimestamp()
        });
        // Update cache
        cacheService.setJournalEntry(date, journalData);
      }
    } else if (!isEmpty) {
      // Only create new document if journal has content
      await setDoc(dailyRef, {
        date,
        journal: {
          ...journalData,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        updatedAt: serverTimestamp()
      });
      // Update cache
      cacheService.setJournalEntry(date, journalData);
    }
    // If empty and no existing document, skip creating (no cache update needed)
  }

  /**
   * Get journal entry for a specific date
   * @param {string} userId - User ID
   * @param {string} date - Date key (YYYY-MM-DD)
   * @param {boolean} useCache - Whether to use cache (default: true)
   * @returns {object|null} - Journal data
   */
  async getJournalEntry(userId, date, useCache = true) {
    // Check cache first
    if (useCache) {
      const cached = cacheService.getJournalEntry(date);
      if (cached) return cached;
    }

    const dailyRef = doc(db, 'calendarData', userId, 'daily', date);
    const snapshot = await getDoc(dailyRef);

    if (snapshot.exists()) {
      const data = snapshot.data();
      const journal = data.journal || null;

      // Cache result
      if (journal) {
        cacheService.setJournalEntry(date, journal);
      }

      return journal;
    }

    return null;
  }

  /**
   * Get journal entries for a date range
   * @param {string} userId - User ID
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @returns {object} - Journal entries indexed by date
   */
  async getJournalEntries(userId, startDate, endDate) {
    const dailyRef = collection(db, 'calendarData', userId, 'daily');
    const q = query(
      dailyRef,
      where('date', '>=', startDate),
      where('date', '<=', endDate)
    );

    const snapshot = await getDocs(q);
    const entries = {};

    snapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.journal) {
        entries[data.date] = data.journal;
        // Cache each entry
        cacheService.setJournalEntry(data.date, data.journal);
      }
    });

    return entries;
  }

  /**
   * Delete journal entry for a specific date
   * @param {string} userId - User ID
   * @param {string} date - Date key (YYYY-MM-DD)
   */
  async deleteJournalEntry(userId, date) {
    const dailyRef = doc(db, 'calendarData', userId, 'daily', date);
    const existing = await getDoc(dailyRef);

    if (existing.exists()) {
      const data = existing.data();
      // If there's other data (canvas, etc.), just remove journal field
      if (data.canvas) {
        await updateDoc(dailyRef, {
          journal: deleteField(),
          updatedAt: serverTimestamp()
        });
      } else {
        // No other data, delete the whole document
        await deleteDoc(dailyRef);
      }
    }

    cacheService.removeJournalEntry(date);
  }

  // ============ CANVAS DATA (Per Time Period) ============

  /**
   * Get collection name for view type
   * @param {string} view - Calendar view type (day, week, month, year)
   * @returns {string} - Collection name
   */
  _getCanvasCollectionName(view) {
    switch (view) {
      case 'day': return 'daily';
      case 'week': return 'weekly';
      case 'month': return 'monthly';
      case 'year': return 'yearly';
      default: return 'daily';
    }
  }

  /**
   * Get key field name for view type
   * @param {string} view - Calendar view type
   * @returns {string} - Field name for the key
   */
  _getCanvasKeyField(view) {
    switch (view) {
      case 'day': return 'date';
      case 'week': return 'weekKey';
      case 'month': return 'monthKey';
      case 'year': return 'yearKey';
      default: return 'date';
    }
  }

  /**
   * Save canvas data for a specific time period
   * @param {string} userId - User ID
   * @param {string} view - Calendar view type (day, week, month, year)
   * @param {string} periodKey - Period key (YYYY-MM-DD, YYYY-Www, YYYY-MM, or YYYY)
   * @param {object} canvasData - Canvas data { elements, version, updatedAt }
   */
  async saveCanvasData(userId, view, periodKey, canvasData) {
    const collectionName = this._getCanvasCollectionName(view);
    const keyField = this._getCanvasKeyField(view);
    const docRef = doc(db, 'calendarData', userId, collectionName, periodKey);
    const existing = await getDoc(docRef);

    if (existing.exists()) {
      await updateDoc(docRef, {
        canvas: canvasData,
        updatedAt: serverTimestamp()
      });
    } else {
      await setDoc(docRef, {
        [keyField]: periodKey,
        canvas: canvasData,
        updatedAt: serverTimestamp()
      });
    }

    // Update cache
    cacheService.setCanvasEntry(view, periodKey, canvasData);
  }

  /**
   * Get canvas data for a specific time period
   * @param {string} userId - User ID
   * @param {string} view - Calendar view type (day, week, month, year)
   * @param {string} periodKey - Period key
   * @param {boolean} useCache - Whether to use cache (default: true)
   * @returns {object|null} - Canvas data
   */
  async getCanvasData(userId, view, periodKey, useCache = true) {
    // Check cache first
    if (useCache) {
      const cached = cacheService.getCanvasEntry(view, periodKey);
      if (cached) return cached;
    }

    const collectionName = this._getCanvasCollectionName(view);
    const docRef = doc(db, 'calendarData', userId, collectionName, periodKey);
    const snapshot = await getDoc(docRef);

    if (snapshot.exists()) {
      const data = snapshot.data();
      const canvas = data.canvas || null;

      if (canvas) {
        cacheService.setCanvasEntry(view, periodKey, canvas);
      }

      return canvas;
    }

    return null;
  }

  /**
   * Delete canvas data for a specific time period
   * @param {string} userId - User ID
   * @param {string} view - Calendar view type (day, week, month, year)
   * @param {string} periodKey - Period key
   */
  async deleteCanvasData(userId, view, periodKey) {
    const collectionName = this._getCanvasCollectionName(view);
    const docRef = doc(db, 'calendarData', userId, collectionName, periodKey);
    const existing = await getDoc(docRef);

    if (existing.exists()) {
      const data = existing.data();
      // If there's other data (journal for daily), just remove canvas field
      if (view === 'day' && data.journal) {
        await updateDoc(docRef, {
          canvas: deleteField(),
          updatedAt: serverTimestamp()
        });
      } else {
        // No other data, delete the whole document
        await deleteDoc(docRef);
      }
    }

    cacheService.removeCanvasEntry(view, periodKey);
  }

  // ============ FULL USER DATA LOAD (Optimized) ============

  /**
   * Load all user data on login (with caching)
   * @param {string} userId - User ID
   * @param {boolean} forceRefresh - Skip cache and fetch from Firestore
   * @returns {object} - All user data
   */
  async loadUserData(userId, forceRefresh = false) {
    // Check cache first (unless force refresh)
    if (!forceRefresh && cacheService.hasUser()) {
      const cachedUser = cacheService.getUser();
      const cachedAccounts = cacheService.getLinkedAccounts();
      const cachedTasks = cacheService.getTaskLists();

      return {
        user: cachedUser,
        linkedAccounts: cachedAccounts || [],
        taskLists: cachedTasks || [],
        fromCache: true
      };
    }

    // Fetch from Firestore
    const [user, linkedAccounts, taskLists] = await Promise.all([
      this.getUser(userId),
      this.getLinkedAccountsFromSubcollection(userId),
      this.getTaskLists(userId, false) // Skip cache for fresh data
    ]);

    // Cache all data
    if (user) cacheService.setUser(user);
    cacheService.setLinkedAccounts(linkedAccounts);
    cacheService.setTaskLists(taskLists);

    return {
      user,
      linkedAccounts,
      taskLists,
      fromCache: false
    };
  }

  // ============ USER STATS ============

  // Set user's experiences count (total events in grownupscalendar2026)
  async setExperiencesCount(userId, count) {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      experiencesCount: count,
      updatedAt: serverTimestamp()
    });
  }

  // Increment user's experiences count
  async incrementExperiencesCount(userId) {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      experiencesCount: increment(1),
      updatedAt: serverTimestamp()
    });
  }

  // Decrement user's experiences count
  async decrementExperiencesCount(userId) {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      const current = userDoc.data().experiencesCount || 0;
      if (current > 0) {
        await updateDoc(userRef, {
          experiencesCount: increment(-1),
          updatedAt: serverTimestamp()
        });
      }
    }
  }

  // Increment user's share count
  async incrementUserShare(userId) {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      shareCount: increment(1),
      updatedAt: serverTimestamp()
    });
  }

  // Increment user's personal sticker count
  async incrementUserStickers(userId) {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      userPersonalStickers: increment(1),
      updatedAt: serverTimestamp()
    });
  }

  // Decrement user's personal sticker count
  async decrementUserStickers(userId) {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      const current = userDoc.data().userPersonalStickers || 0;
      if (current > 0) {
        await updateDoc(userRef, {
          userPersonalStickers: increment(-1),
          updatedAt: serverTimestamp()
        });
      }
    }
  }

  // Increment user's adventure/experiences count (called when event tracked)
  async incrementUserAdventure(userId) {
    await this.incrementExperiencesCount(userId);
  }

  // Decrement user's adventure/experiences count (called when event untracked)
  async decrementUserAdventure(userId) {
    await this.decrementExperiencesCount(userId);
  }

  // ============ USER EVENTS ============

  // Add event to user's list
  async addUserEvent(userId, eventId) {
    const userEventsRef = doc(db, 'user_events', userId);
    await setDoc(userEventsRef, {
      eventIds: arrayUnion(eventId),
      updatedAt: serverTimestamp()
    }, { merge: true });
  }

  // Remove event from user's list
  async removeUserEvent(userId, eventId) {
    const userEventsRef = doc(db, 'user_events', userId);
    const existingDoc = await getDoc(userEventsRef);
    if (existingDoc.exists()) {
      await updateDoc(userEventsRef, {
        eventIds: arrayRemove(eventId),
        updatedAt: serverTimestamp()
      });
    }
  }

  // Get user's events
  async getUserEvents(userId) {
    const userEventsRef = doc(db, 'user_events', userId);
    const snapshot = await getDoc(userEventsRef);
    return snapshot.exists() ? snapshot.data().eventIds || [] : [];
  }

  // ============ CALENDAR STATS ============

  // Track calendar usage
  async trackCalendarUsed(calendarId, calendarName, userId = null) {
    const calendarStatsRef = doc(db, 'calendar_stats', calendarId);
    const existingStats = await getDoc(calendarStatsRef);

    if (!existingStats.exists()) {
      await setDoc(calendarStatsRef, {
        name: calendarName,
        eventCount: 1,
        userIds: userId ? [userId] : [],
        userCount: userId ? 1 : 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } else {
      const updates = {
        eventCount: increment(1),
        updatedAt: serverTimestamp()
      };

      // Add user to list if not already there
      if (userId) {
        const currentUserIds = existingStats.data().userIds || [];
        if (!currentUserIds.includes(userId)) {
          updates.userIds = arrayUnion(userId);
          updates.userCount = increment(1);
        }
      }

      await updateDoc(calendarStatsRef, updates);
    }
  }

  // ============ CALENDAR SUBSCRIPTIONS (Phase 5) ============

  // Create or update a user's subscription to a themed calendar
  async createSubscription(calendarId, userId, { displayName, photoURL, selectedEventIds }) {
    const subRef = doc(db, 'calendar_subscriptions', calendarId, 'subscribers', userId);
    const existing = await getDoc(subRef);

    if (existing.exists()) {
      // Update — preserve subscribedAt
      await updateDoc(subRef, {
        displayName: displayName || null,
        photoURL: photoURL || null,
        selectedEventIds,
        updatedAt: serverTimestamp()
      });
    } else {
      // Create
      await setDoc(subRef, {
        userId,
        displayName: displayName || null,
        photoURL: photoURL || null,
        selectedEventIds,
        subscribedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
  }

  // Update selected events for an existing subscription
  async updateSubscription(calendarId, userId, selectedEventIds) {
    const subRef = doc(db, 'calendar_subscriptions', calendarId, 'subscribers', userId);
    await updateDoc(subRef, {
      selectedEventIds,
      updatedAt: serverTimestamp()
    });
  }

  // Delete a user's subscription
  async deleteSubscription(calendarId, userId) {
    const subRef = doc(db, 'calendar_subscriptions', calendarId, 'subscribers', userId);
    await deleteDoc(subRef);
  }

  // Get a single subscription record
  async getSubscription(calendarId, userId) {
    const subRef = doc(db, 'calendar_subscriptions', calendarId, 'subscribers', userId);
    const snapshot = await getDoc(subRef);
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
  }

  // Get recent subscribers for a calendar (for social proof)
  async getRecentSubscribers(calendarId, maxResults = 10) {
    const subsRef = collection(db, 'calendar_subscriptions', calendarId, 'subscribers');
    const q = query(subsRef, orderBy('subscribedAt', 'desc'), limit(maxResults));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  // Get subscriber count for a calendar
  async getSubscriberCount(calendarId) {
    const subsRef = collection(db, 'calendar_subscriptions', calendarId, 'subscribers');
    const snapshot = await getDocs(subsRef);
    return snapshot.size;
  }

  // Get all subscriptions for a user across all calendars (collection group query)
  async getMySubscriptions(userId) {
    const q = query(
      collectionGroup(db, 'subscribers'),
      where('userId', '==', userId),
      orderBy('subscribedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({
      id: d.id,
      calendarId: d.ref.parent.parent.id,
      ...d.data()
    }));
  }

  // ============ CALENDAR STATS — DENORMALIZED COUNTERS (Phase 5.06) ============

  // Get denormalized stats for a calendar (single doc read)
  async getCalendarStats(calendarId) {
    const statsRef = doc(db, 'calendar_stats', calendarId);
    const snapshot = await getDoc(statsRef);
    return snapshot.exists() ? snapshot.data() : null;
  }

  // Atomically increment subscriber count + update sample + event participation
  async incrementSubscriberStats(calendarId, user, selectedEventIds) {
    const statsRef = doc(db, 'calendar_stats', calendarId);

    await runTransaction(db, async (transaction) => {
      const statsDoc = await transaction.get(statsRef);

      if (!statsDoc.exists()) {
        // First subscriber — create the document
        const eventParticipation = {};
        for (const id of selectedEventIds) {
          eventParticipation[id] = 1;
        }
        transaction.set(statsRef, {
          subscriberCount: 1,
          subscriberSample: [{
            userId: user.uid,
            displayName: user.displayName || null,
            photoURL: user.photoURL || null,
          }],
          eventParticipation,
          updatedAt: serverTimestamp(),
        });
      } else {
        const data = statsDoc.data();
        const updates = {
          subscriberCount: increment(1),
          updatedAt: serverTimestamp(),
        };

        // Update sample if under 5 entries
        if ((data.subscriberSample || []).length < 5) {
          updates.subscriberSample = arrayUnion({
            userId: user.uid,
            displayName: user.displayName || null,
            photoURL: user.photoURL || null,
          });
        }

        // Increment each selected event count
        for (const eventId of selectedEventIds) {
          updates[`eventParticipation.${eventId}`] = increment(1);
        }

        transaction.update(statsRef, updates);
      }
    });
  }

  // Atomically decrement subscriber count + event participation
  async decrementSubscriberStats(calendarId, userId, selectedEventIds) {
    const statsRef = doc(db, 'calendar_stats', calendarId);

    await runTransaction(db, async (transaction) => {
      const statsDoc = await transaction.get(statsRef);
      if (!statsDoc.exists()) return;

      const data = statsDoc.data();
      const currentCount = data.subscriberCount || 0;
      const updates = {
        subscriberCount: currentCount > 0 ? increment(-1) : 0,
        updatedAt: serverTimestamp(),
      };

      // Decrement each selected event count
      for (const eventId of selectedEventIds) {
        const currentEventCount = (data.eventParticipation || {})[eventId] || 0;
        if (currentEventCount > 0) {
          updates[`eventParticipation.${eventId}`] = increment(-1);
        }
      }

      transaction.update(statsRef, updates);

      // If user was in sample, rebuild it after transaction
      const inSample = (data.subscriberSample || []).some(s => s.userId === userId);
      if (inSample) {
        // Schedule sample rebuild outside transaction
        setTimeout(() => this.rebuildSubscriberSample(calendarId), 100);
      }
    });
  }

  // Update event participation counts when subscription selection changes
  async updateEventParticipationCounts(calendarId, addedEventIds, removedEventIds) {
    const statsRef = doc(db, 'calendar_stats', calendarId);
    const updates = { updatedAt: serverTimestamp() };

    for (const id of addedEventIds) {
      updates[`eventParticipation.${id}`] = increment(1);
    }
    for (const id of removedEventIds) {
      updates[`eventParticipation.${id}`] = increment(-1);
    }

    await updateDoc(statsRef, updates);
  }

  // Rebuild subscriber sample from actual subscriber documents
  async rebuildSubscriberSample(calendarId) {
    const subsRef = collection(db, 'calendar_subscriptions', calendarId, 'subscribers');
    const q = query(subsRef, orderBy('subscribedAt', 'desc'), limit(5));
    const snapshot = await getDocs(q);

    const sample = snapshot.docs.map(d => ({
      userId: d.data().userId,
      displayName: d.data().displayName || null,
      photoURL: d.data().photoURL || null,
    }));

    const statsRef = doc(db, 'calendar_stats', calendarId);
    await updateDoc(statsRef, {
      subscriberSample: sample,
      updatedAt: serverTimestamp(),
    });
  }

  // ============ GLOBAL STATS ============

  // Increment visitor count (call once per unique visitor)
  async incrementVisitorCount() {
    const statsRef = doc(db, 'stats', 'global');
    const existingStats = await getDoc(statsRef);

    if (!existingStats.exists()) {
      await setDoc(statsRef, {
        visitorCount: 1,
        totalEventsAdded: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } else {
      await updateDoc(statsRef, {
        visitorCount: increment(1),
        updatedAt: serverTimestamp()
      });
    }
  }

  // Increment total events added
  async incrementGlobalEventCount() {
    const statsRef = doc(db, 'stats', 'global');
    const existingStats = await getDoc(statsRef);

    if (!existingStats.exists()) {
      await setDoc(statsRef, {
        visitorCount: 0,
        totalEventsAdded: 1,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } else {
      await updateDoc(statsRef, {
        totalEventsAdded: increment(1),
        updatedAt: serverTimestamp()
      });
    }
  }

  // Get global stats
  async getGlobalStats() {
    const statsRef = doc(db, 'stats', 'global');
    const snapshot = await getDoc(statsRef);
    return snapshot.exists() ? snapshot.data() : { visitorCount: 0, totalEventsAdded: 0 };
  }

  // ============ MOBILE EMAIL COLLECTION ============

  // Save mobile email for follow-up
  async saveMobileEmail(email) {
    const emailRef = doc(collection(db, 'mobile_emails'));
    await setDoc(emailRef, {
      email,
      timestamp: serverTimestamp(),
      userAgent: navigator.userAgent,
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight
    });
    return emailRef.id;
  }

  // ============ WISHLIST COLLECTION ============

  // Save user to wishlist
  async saveToWishlist(email, name = null) {
    const wishlistRef = await addDoc(collection(db, 'wishlist'), {
      email,
      name: name || null,
      timestamp: serverTimestamp(),
      userAgent: navigator.userAgent,
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
      source: 'landing_page'
    });
    return wishlistRef.id;
  }

  // ============ UTILITIES ============

  // Generate a short, URL-friendly share ID
  generateShareId() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }
}

// Export singleton instance
export const firestoreService = new FirestoreService();

export default firestoreService;
